import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { DetailsCardHint } from '~/components/DetailsCard';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { Image3D } from '~/domain/patient/image3d';
import {
    domainEventBus,
    RegistrationCreateRequestedEvent,
} from '~/domain/events';
import usePrimaryImage, { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useSecondaryImage from '~/hooks/image/useSecondaryImage';
import { useRegistrationState } from '~/hooks/registration/useRegistrationStore';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

import DatasetPicker from '../ContouringStep/DatasetPicker/DatasetPicker';
import StructureSet from '../ContouringStep/StructureSet/StructureSet';
import StepHeader from '../StepHeader';
import ImageList from './ImageRegistrations/components/ImageList';
import RegistrationPanel from './ImageRegistrations/RegistrationPanel';

export default function RegistrationContent() {
    const { primaryImage, availableImages } = usePrimaryImage(true);

    if (!primaryImage) {
        return (
            <Box>
                <StepHeader titleId="workflow.imageRegistration" />
                <Box px={2} mt={1}>
                    <DatasetPicker />
                </Box>
            </Box>
        );
    }

    return (
        <Box height="100%" display="flex" flexDirection="column">
            <RegistrationContentInner
                primaryImage={primaryImage}
                availableImages={availableImages}
            />
        </Box>
    );
}

interface RegistrationContentInnerProps {
    primaryImage: Image3D;
    availableImages: AvailableImage[];
}

function RegistrationContentInner({
    primaryImage,
    availableImages,
}: RegistrationContentInnerProps) {
    const [selectedImageUid, setSelectedImageUid] = useState<
        string | undefined
    >();
    const [createRegImage, setCreateRegImage] = useState<
        AvailableImage | undefined
    >();
    const intl = useIntl();

    const registrationState = useRegistrationState();
    const { handleChangeSecondaryImage } = useSecondaryImage();
    const fusion = useActiveFusionTarget();
    const fusedImage = fusion.secondaryImage;
    const structureSet = useRecoilValue(currentStructureSetState);

    // On entering the registration tab, clear a blend displayed through the
    // inverse of a registration. Such a blend renders (the pipeline inverts
    // the matrix) but a drag from the primary would edit the registration
    // backwards, so it must not be the active pair here.
    useEffect(() => {
        const displayed = fusion.displayedRegistration;
        if (
            displayed &&
            fusedImage &&
            !displayed.editableFrom(primaryImage.frameOfReferenceUid)
        ) {
            handleChangeSecondaryImage(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Sync from registration store when a registration is active
        if (registrationState.selectedSopInstanceUid) {
            const targetImageUid = registrationState.selectedImageSeriesUid;
            if (targetImageUid && targetImageUid !== selectedImageUid) {
                setSelectedImageUid(targetImageUid);
            }
            return;
        }

        // Sync from secondary image when no registration is active
        // (e.g. same-FOR fusion set from another step)
        const secondarySeriesUid = fusedImage?.seriesUid;
        if (secondarySeriesUid && secondarySeriesUid !== selectedImageUid) {
            setSelectedImageUid(secondarySeriesUid);
        } else if (!secondarySeriesUid && selectedImageUid) {
            setSelectedImageUid(undefined);
        }
    }, [registrationState, fusedImage, selectedImageUid]);

    const hasRegistrationsForImage = useCallback(
        (image: AvailableImage): boolean =>
            (
                registrationState.imageRegistrations.find(
                    (r) => r.imageSeriesUid === image.seriesInstanceUID
                )?.registrations ?? []
            ).length > 0,
        [registrationState.imageRegistrations]
    );

    const handleSelectImage = useCallback(
        (image: AvailableImage) => {
            const isDeselecting = selectedImageUid === image.seriesInstanceUID;

            handleChangeSecondaryImage(null);

            if (isDeselecting) {
                setSelectedImageUid(undefined);
                return;
            }

            setSelectedImageUid(image.seriesInstanceUID);

            // If image is loaded, has different FOR, and no
            // registrations exist, prompt to create one.
            const sharesFOR =
                image.imageObject?.frameOfReferenceUid ===
                primaryImage.frameOfReferenceUid;
            if (
                image.imageObject &&
                !sharesFOR &&
                !hasRegistrationsForImage(image)
            ) {
                setCreateRegImage(image);
            }
        },
        [
            selectedImageUid,
            primaryImage,
            handleChangeSecondaryImage,
            hasRegistrationsForImage,
        ]
    );

    const handleConfirmCreateRegistration = useCallback(() => {
        if (createRegImage?.imageObject) {
            domainEventBus.publish(
                new RegistrationCreateRequestedEvent(
                    primaryImage.seriesUid,
                    createRegImage.imageObject.seriesUid
                )
            );
        }
        setCreateRegImage(undefined);
    }, [createRegImage, primaryImage.seriesUid]);

    const handleCancelCreateRegistration = useCallback(() => {
        setCreateRegImage(undefined);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setSelectedImageUid(undefined);
        handleChangeSecondaryImage(null);
    }, [handleChangeSecondaryImage]);

    const selectedImage = useMemo(
        () =>
            availableImages.find(
                (img) => img.seriesInstanceUID === selectedImageUid
            ),
        [availableImages, selectedImageUid]
    );

    const isPrimarySelected = selectedImageUid === primaryImage.seriesUid;
    // A registration is what the list selects: it blends the pair and opens
    // for editing. With none yet, New registration is the way on instead.
    const hasRegistrations = registrationState.imageRegistrations.some(
        (entry) => entry.registrations.length > 0
    );

    return (
        <Box
            position="relative"
            display="grid"
            // Of what the panel leaves, the images take what they need up to a
            // share, and the structures the rest.
            gridTemplateRows={
                structureSet
                    ? 'auto fit-content(40%) minmax(0, 1fr) auto'
                    : 'auto minmax(0, 1fr) auto'
            }
            gridTemplateColumns="minmax(0, 1fr)"
            gap={1}
            sx={{ overflowX: 'hidden', height: '100%' }}
        >
            <Box display="grid" gridTemplateColumns="minmax(0, 1fr)" gap={1}>
                <StepHeader titleId="workflow.imageRegistration" />
                <Box px={2}>
                    <DatasetPicker />
                </Box>
            </Box>
            <Box sx={{ minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                <ImageList
                    primaryImage={primaryImage}
                    availableImages={availableImages}
                    selectedImageUid={selectedImageUid}
                    onSelectImage={handleSelectImage}
                />
            </Box>

            {structureSet && (
                <Box height="100%" display="grid" sx={{ minHeight: 0 }}>
                    <StructureSet structureSet={structureSet} visibilityOnly />
                </Box>
            )}

            {selectedImage && selectedImageUid ? (
                <RegistrationPanel
                    image={selectedImage}
                    primaryImage={primaryImage}
                    isPrimary={isPrimarySelected}
                    onClose={handleCloseDetails}
                />
            ) : (
                hasRegistrations && (
                    <DetailsCardHint data-testid="registration-select-hint">
                        <FormattedMessage id="workflow.imageRegistration.selectRegistrationToStart" />
                    </DetailsCardHint>
                )
            )}

            <ConfirmationDialog
                open={!!createRegImage}
                title={intl.formatMessage({
                    id: 'workflow.imageRegistration.notRegisteredTitle',
                })}
                message={intl.formatMessage({
                    id: 'workflow.imageRegistration.notRegistered',
                })}
                confirmText={intl.formatMessage({
                    id: 'workflow.imageRegistration.newRegistration',
                })}
                onConfirm={handleConfirmCreateRegistration}
                onCancel={handleCancelCreateRegistration}
                mode="primary"
            />
        </Box>
    );
}
